import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createProduct } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Package, GraduationCap, BookOpen, Users, Layers, Wrench, Phone, Coffee } from "lucide-react";

const PRODUCT_TYPES = [
  {
    key: "DigitalProduct",
    name: "Digital product",
    description: "Any set of files to download or stream.",
    icon: Package,
    category: "Products"
  },
  {
    key: "CourseProduct",
    name: "Course or tutorial",
    description: "Sell a single lesson or teach a whole cohort of students.",
    icon: GraduationCap,
    category: "Products"
  },
  {
    key: "EbookProduct",
    name: "E-book",
    description: "Offer a book or comic in PDF, ePub, and Mobi formats.",
    icon: BookOpen,
    category: "Products"
  },
  {
    key: "MembershipProduct",
    name: "Membership",
    description: "Start a membership business around your fans.",
    icon: Users,
    category: "Products"
  },
  {
    key: "BundleProduct",
    name: "Bundle",
    description: "Sell two or more existing products for a new price",
    icon: Layers,
    category: "Products"
  },
  {
    key: "CommissionProduct",
    name: "Commission",
    description: "Sell custom services with 50% deposit upfront, 50% upon completion.",
    icon: Wrench,
    category: "Services"
  },
  {
    key: "CallProduct",
    name: "Call",
    description: "Offer scheduled calls with your customers.",
    icon: Phone,
    category: "Services"
  },
  {
    key: "CoffeeProduct",
    name: "Coffee",
    description: "Boost your support and accept tips from customers.",
    icon: Coffee,
    category: "Services"
  }
];

const ProductNew: React.FC = () => {
  const { siteSlug } = useParams<{ siteSlug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    amount: ""
  });

  const mutation = useMutation({
    mutationFn: (data: any) => createProduct(siteSlug!, data),
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ["products", siteSlug] });
      toast({
        title: "Product created",
        description: "Your product has been created successfully.",
      });
      navigate(`/sites/${siteSlug}/products/${product.id}/edit`);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create product",
        variant: "destructive",
      });
    },
  });

  const handleTypeSelect = (type: string) => {
    setSelectedType(type);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType) return;
    
    mutation.mutate({
      type: selectedType,
      name: formData.name,
      description: formData.description,
      amount: parseFloat(formData.amount) || 0,
    });
  };

  const handleBack = () => {
    navigate(`/sites/${siteSlug}/products`);
  };

  const productsByCategory = PRODUCT_TYPES.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {} as Record<string, typeof PRODUCT_TYPES>);

  return (
    <div className="p-6 max-w-6xl- mx-auto-">
      <Button variant="ghost" onClick={handleBack} className="mb-6">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Products
      </Button>

      <h1 className="text-3xl font-bold mb-8">Create a New Product</h1>

      {!selectedType ? (
        <>
          <h2 className="text-xl font-semibold mb-4">Select Product Type</h2>
          
          {Object.entries(productsByCategory).map(([category, products]) => (
            <div key={category} className="mb-8">
              <h3 className="text-lg font-semibold text-muted-foreground mb-3">{category}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {products.map((productType) => {
                  const Icon = productType.icon;
                  return (
                    <Card
                      key={productType.key}
                      className="cursor-pointer hover:border-primary transition-colors"
                      onClick={() => handleTypeSelect(productType.key)}
                    >
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <Icon className="h-6 w-6 text-primary" />
                          <CardTitle className="text-lg">{productType.name}</CardTitle>
                        </div>
                        <CardDescription>{productType.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Product Details</CardTitle>
            <CardDescription>
              Creating a {PRODUCT_TYPES.find(t => t.key === selectedType)?.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter product name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter product description"
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="amount">Price ($) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="flex gap-4">
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? "Creating..." : "Create Product"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setSelectedType(null)}>
                  Change Type
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductNew;
